// Minimal markdown -> HTML renderer shared by ticket reply, ticket comment
// rendering, and the new-ticket modal. Output is Tailwind-styled.
const parseMarkdown = (text) => {
  let parsed = text;

  // Code blocks (must be before inline code)
  parsed = parsed.replace(
    /```([\w]*)?\n([\s\S]*?)```/g,
    (match, lang, code) => {
      const language = lang || "plaintext";

      return `<pre class="bg-gray-900 dark:bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto my-2"><code class="language-${language}">${code
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</code></pre>`;
    }
  );

  // Tables
  parsed = parsed.replace(
    /(\|[^\n]+\|\n)(\|[\s:-]+\|\n)((?:\|[^\n]+\|\n?)+)/g,
    (match, header, separator, body) => {
      const headers = header
        .trim()
        .split("|")
        .filter((h) => h.trim());
      const rows = body
        .trim()
        .split("\n")
        .map((row) =>
          row.split("|").filter((cell) => cell !== undefined && cell !== "")
        );

      let table =
        '<table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700 my-2">';

      table += '<thead class="bg-gray-50 dark:bg-gray-800"><tr>';
      headers.forEach((h) => {
        table += `<th class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">${h.trim()}</th>`;
      });
      table +=
        '</tr></thead><tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">';
      rows.forEach((row) => {
        table += "<tr>";
        row.forEach((cell) => {
          table += `<td class="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">${cell.trim()}</td>`;
        });
        table += "</tr>";
      });
      table += "</tbody></table>";

      return table;
    }
  );

  // Horizontal rules
  parsed = parsed.replace(
    /^---$/gm,
    '<hr class="my-4 border-gray-300 dark:border-gray-600" />'
  );

  // Headers (must be before bold)
  parsed = parsed.replace(
    /^### (.+)$/gm,
    '<h3 class="text-lg font-semibold mt-3 mb-2">$1</h3>'
  );
  parsed = parsed.replace(
    /^## (.+)$/gm,
    '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>'
  );
  parsed = parsed.replace(
    /^# (.+)$/gm,
    '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>'
  );

  // Checkboxes
  parsed = parsed.replace(/- \[([ x])\] (.+)/g, (match, checked, text) => {
    const isChecked = checked === "x";

    return `<div class="flex items-center gap-2 my-1">
        <input type="checkbox" ${isChecked ? "checked" : ""} disabled class="rounded border-gray-300 dark:border-gray-600" />
        <span class="${isChecked ? "line-through text-gray-500" : ""}">${text}</span>
      </div>`;
  });

  // Lists (must be after checkboxes)
  // Numbered lists
  parsed = parsed.replace(/^(\d+\. .+(?:\n(?!\n)(?!\d+\. ).+)*)/gm, (match) => {
    const items = match
      .split("\n")
      .map((item) => item.replace(/^\d+\. (.+)/, "<li>$1</li>"))
      .join("");

    return `<ol class="list-decimal list-inside my-2 space-y-1">${items}</ol>`;
  });

  // Bullet lists
  parsed = parsed.replace(/^((?:- (?!\[).+(?:\n|$))+)/gm, (match) => {
    const items = match
      .trim()
      .split("\n")
      .map((item) => item.replace(/^- (.+)/, "<li>$1</li>"))
      .join("");

    return `<ul class="list-disc list-inside my-2 space-y-1">${items}</ul>`;
  });

  // Blockquotes
  parsed = parsed.replace(
    /^> (.+)$/gm,
    '<blockquote class="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-1 my-2 italic text-gray-600 dark:text-gray-400">$1</blockquote>'
  );

  // Bold (must be before italic)
  parsed = parsed.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  // Italic
  parsed = parsed.replace(/\*([^*]+)\*/g, "<em>$1</em>");

  // Underline
  parsed = parsed.replace(/<u>(.*?)<\/u>/g, "<u>$1</u>");

  // Strikethrough
  parsed = parsed.replace(/~~(.*?)~~/g, "<del>$1</del>");

  // Inline code
  parsed = parsed.replace(
    /`([^`]+)`/g,
    '<code class="bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono">$1</code>'
  );

  // Links
  parsed = parsed.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" class="text-primary-600 hover:text-primary-700 underline">$1</a>'
  );

  // Line breaks
  parsed = parsed.replace(/\n/g, "<br />");

  return parsed;
};

export default parseMarkdown;
